/**
 * The compact summary card of one publish attempt (Registry page): overall
 * progress, the step that is running now, and the four independently
 * published benchmark components (fixtures, glibc, musl, macOS). Pure
 * logic over the 12-step view (publishSteps.ts) so the card, the progress
 * bar and the tests all read the same numbers; the full step list lives in
 * the detail drawer (components/registry/AttemptDetailDrawer.tsx).
 */
import {
  publishStepsView,
  type AttemptInput,
  type PublishStepRow,
  type StepState,
} from "./publishSteps.ts";

/** The four benchmark components, in publication order. */
export const COMPONENTS = [
  { key: "benchmark-fixtures", label: "fixtures" },
  { key: "benchmark-glibc", label: "glibc" },
  { key: "benchmark-musl", label: "musl" },
  { key: "benchmark-mac", label: "mac" },
] as const;

export interface ComponentChip {
  key: string;
  label: string;
  state: StepState;
  /** "pending" | "running" | "done" | "failed" | "degraded" | "skipped" */
  stateLabel: string;
  /** Raw reason (tooltip), when the component has one. */
  reason: string | null;
}

export interface CurrentStep {
  n: string;
  title: string;
  state: StepState;
  /** Time since it started (ms), null when unknown. */
  elapsedMs: number | null;
  /** When it started (ms) while it is still running: the card shows a live
   *  isolated timer from this instead of re-rendering on every tick. */
  startedAt: number | null;
  progress: string | null;
  /** Other steps running at the same time ("+1 more"). */
  alsoRunning: number;
}

export interface AttemptSummary {
  /** Steps that have settled (anything but pending / running). */
  done: number;
  total: number;
  /** One state per counted step, in display order (the segmented bar). */
  segments: StepState[];
  current: CurrentStep | null;
  components: ComponentChip[];
  /** Steps that are degraded or failed. */
  problems: number;
}

/** The rows counted as "steps": every row except the parents whose sub-rows
 *  are the real units (Publish -> 10a, Benchmark pages -> 12a-12d) and the
 *  per-job sub-rows of the combined test build (1.1, 1.2). */
export function countedSteps(
  rows: readonly PublishStepRow[],
): PublishStepRow[] {
  const out: PublishStepRow[] = [];
  const walk = (list: readonly PublishStepRow[]) => {
    for (const r of list) {
      const parentOnly = r.key === "publish" || r.key === "benchmark-pages";
      const jobRow = /^\d+\.\d+$/.test(r.n);
      if (!parentOnly && !jobRow) out.push(r);
      walk(r.subrows);
    }
  };
  walk(rows);
  return out;
}

const CHIP_LABEL: Record<StepState, string> = {
  pending: "pending",
  running: "running",
  ok: "done",
  degraded: "degraded",
  failed: "failed",
  skipped: "skipped",
};

export function chipLabel(state: StepState): string {
  return CHIP_LABEL[state];
}

/** null when the attempt has no step detail (an older publisher). */
export function attemptSummary(
  input: AttemptInput,
  nowMs: number = Date.now(),
): AttemptSummary | null {
  const view = publishStepsView(input, nowMs);
  if (!view) return null;
  const steps = countedSteps(view.rows);
  const settled = (s: StepState) => s !== "pending" && s !== "running";
  const running = steps.filter((s) => s.state === "running");
  // The decoupled darwin build can run for hours after everything else is
  // done: it is only "the current step" when nothing else is.
  const main = running.filter((s) => s.key !== "darwin-followup");
  const pick = main[0] ?? running[0];
  const all = view.rows.flatMap((r) => [r, ...r.subrows]);
  const byKey = new Map(all.map((r) => [r.key, r] as const));
  return {
    done: steps.filter((s) => settled(s.state)).length,
    total: steps.length,
    segments: steps.map((s) => s.state),
    current: pick
      ? {
          n: pick.n,
          title: pick.title,
          state: pick.state,
          elapsedMs:
            pick.durationMs ??
            (pick.startedAt !== null
              ? Math.max(0, nowMs - pick.startedAt)
              : null),
          progress: pick.progress,
          startedAt: pick.durationMs === null ? pick.startedAt : null,
          alsoRunning: running.length - 1,
        }
      : null,
    components: COMPONENTS.map((c) => {
      const r = byKey.get(c.key);
      const state: StepState = r?.state ?? "pending";
      return {
        key: c.key,
        label: c.label,
        state,
        stateLabel: chipLabel(state),
        reason: r?.reason ?? null,
      };
    }),
    problems: steps.filter(
      (s) => s.state === "degraded" || s.state === "failed",
    ).length,
  };
}
